import React, { MouseEvent, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { ColumnType } from "antd/es/table";

import { Activity } from "@mapmycustomers/shared/types/entity";

import Table from "@app/component/Table";
import { fetchChildActivities } from "@app/scene/activity/store/actions";
import { getChildActivities } from "@app/scene/activity/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./ChildActivitiesTable.module.scss";

export interface ChildActivitiesTableProps {
  columns: ColumnType<Activity>[];
  onActivityClick?: (activity: Activity) => void;
  parentActivityId: Activity["id"];
}

const ChildActivitiesTable: React.FC<
  ChildActivitiesTableProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps
> = ({ childActivities, columns, onActivityClick, onFetchChildActivities, parentActivityId }) => {
  const analytics = useAnalytics();

  useEffect(() => {
    onFetchChildActivities({ parentActivityId });
  }, [onFetchChildActivities, parentActivityId]);

  const childColumns = useMemo(() => {
    return columns.map((column) =>
      column.key === "checkbox" ? { ...column, width: (column.width! as number) + 50 } : column
    );
  }, [columns]);

  const rowEventHandlers = useCallback(
    (record: Activity) => ({
      onClick: (e: MouseEvent<HTMLTableCellElement>) => {
        // This is not a good solution but the problem here is that this get triggered if we preview some
        // file by clicking file icon on every activity. Please suggest a better way to do this.
        if (
          e.currentTarget.classList?.contains("ant-table-cell") ||
          e.currentTarget.closest(".ant-table-cell")
        ) {
          onActivityClick?.(record);
        }
        analytics.clicked(["Child Activity in Table Row"]);
      },
    }),
    [onActivityClick, analytics]
  );

  return (
    <div>
      <Table<Activity>
        bordered={false}
        className={styles.table}
        clickableRows
        columns={childColumns}
        dataSource={childActivities}
        loading={!childActivities.length}
        onRow={rowEventHandlers}
        pagination={false}
        rowKey="id"
        scrollable={false}
        showBorder={false}
        showHeader={false}
        tableLayout="fixed"
      />
    </div>
  );
};

const mapStateToProps = (state: RootState, ownProps: ChildActivitiesTableProps) => ({
  childActivities: getChildActivities(state)(ownProps.parentActivityId),
});

const mapDispatchToProps = {
  onFetchChildActivities: fetchChildActivities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChildActivitiesTable);
